<template>
  <div class="view">
    <div class="header-container">
      <div class="view-gabarit">
        <h2>{{ $t('exportSection.header') }}</h2>
      </div>
    </div>
    <div class="form-container">
      <div class="view-gabarit">
        <div class="view-content">
          <div class="details-box">
            <h3>1. {{ $t('exportSection.filtersTitle') }}</h3>
            <p class="baseline">{{ $t('exportSection.filtersBaseline') }}</p>

            <div class="counter-container">
              <div class="counter">
                <AnimatedValue class="active" :value="accountedAddresses" />
                <span class="divider">/</span>
                <span class="total">{{ totalAddresses }}</span>
              </div>
              <div class="text">
                {{ $t('studyCreation.accountedAddresses', accountedAddresses) }}
              </div>
            </div>
          </div>

          <div class="form-gabarit">
            <div class="form-block">
              <CheckBoxFilter v-if="filters.favorite_transports" identifier="favorite_transports" :segment="filters.favorite_transports" />
              <CheckBoxFilter v-if="filters.services" identifier="services" :segment="filters.services" />
            </div>
            <div class="form-block">
              <CheckBoxFilter v-if="filters.contract_types" identifier="contract_types" :segment="filters.contract_types" />
              <CheckBoxFilter v-if="filters.seniorities" identifier="seniorities" :segment="filters.seniorities" />
            </div>
            <div class="form-block">
              <CheckBoxFilter v-if="filters.filter1" :custom="true" :identifier="currentExport.filters.filter1_name || 'Filtre 1'" :segment="filters.filter1" />
              <CheckBoxFilter v-if="filters.filter2" :custom="true" :identifier="currentExport.filters.filter2_name || 'Filtre 2'" :segment="filters.filter2" />
            </div>
          </div>
        </div>
      </div>
      <div class="view-gabarit">
        <div class="cta-container">
          <button class="study-button" @click="() => previousStep()">{{ $t('previous') }}</button>
          <button class="study-button main" @click="() => nextStep()">{{ $t('continue') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CheckBoxFilter from './pure/CheckBoxFilter';
import AnimatedValue from '@/components/AnimatedValue';

export default {
  name: 'FilterExport',

  components: {
    CheckBoxFilter,
    AnimatedValue,
  },

  data() {
    return {
      accountedAddresses: 0,
    };
  },

  computed: {
    ...mapGetters({
      currentExport: 'currentExport',
      study: 'study',
      collaborators: 'collaborators',
    }),

    filters() {
      const filters = {};
      for (const key in this.currentExport.filters) {
        if (this.currentExport.filters[key] && typeof this.currentExport.filters[key] !== 'string') {
          if (this.currentExport.filters[key].length > 1) {
            filters[key] = this.currentExport.filters[key];
          }
        }
      }
      return filters;
    },

    totalAddresses() {
      return this.currentExport.total_valid_collaborators;
    },
  },

  watch: {
    filters: {
      handler(value) {
        this.computeSite(value);
      },

      deep: true,
    },
  },

  mounted() {
    this.accountedAddresses = this.collaborators.length;
    this.computeSite(this.filters);

    if (Object.keys(this.filters).length === 0) {
      this.nextStep();
    }
  },

  methods: {
    getActiveFilters() {
      const filters = {};

      for (const key in this.currentExport.filters) {
        if (this.currentExport.filters[key] && typeof this.currentExport.filters[key] !== 'string') {
          if (this.currentExport.filters[key].length > 1) {
            this.currentExport.filters[key].forEach(item => {
              if (!filters[key]) {
                filters[key] = [];
              }
              if (item.enabled) {
                filters[key].push(item.value);
              }
            });
          }
        }
      }
      return filters;
    },

    previousStep() {
      this.$router.push({ name: 'studyList' });
    },

    nextStep() {
      const id = this.$route.params.studyId;
      const payload = {
        filters: this.getActiveFilters(),
        status: 2,
      };
      this.$store
        .dispatch('patchExport', {
          id,
          payload,
        })
        .then(() => {
          this.$router.push({ name: 'exportSites' });
        });
    },

    computeSite(value) {
      const fullFilters = {
        contract_types: value.contract_types ? value.contract_types.filter(e => e.enabled).map(e => e.value) : null,
        filter1: value.filter1 ? value.filter1.filter(e => e.enabled).map(e => e.value.toLowerCase() || 'Non renseigné'.toLowerCase()) : null,
        filter2: value.filter2 ? value.filter2.filter(e => e.enabled).map(e => e.value.toLowerCase() || 'Non renseigné'.toLowerCase()) : null,
        favorite_transports: value.favorite_transports
          ? value.favorite_transports
              .filter(e => e.enabled)
              .flatMap(e => {
                if (e.value === 'None') return ['none'];
                if (e.value === 'Transport') return ['public', 'public2025', 'public2030', 'public2027', 'public2031'];
                if (e.value === 'Car') return ['private', 'private_without_toll'];
                if (e.value === 'Bike') return ['bicycle'];
                if (e.value === 'Walking') return ['walking'];
                return [e.value];
              })
          : null,
        services: value.services ? value.services.filter(e => e.enabled).map(e => e.value) : null,
        seniorities: value.seniorities ? value.seniorities.filter(e => e.enabled).map(e => e.value) : null,
      };

      const filteredCollaborators = this.collaborators.filter(e => {
        const contractTypeFilter = fullFilters.contract_types ? fullFilters.contract_types.includes(e.details.contractType) : true;
        const filter1Filter = fullFilters.filter1 ? fullFilters.filter1.includes(e.details.filter1.toLowerCase()) : true;
        const filter2Filter = fullFilters.filter2 ? fullFilters.filter2.includes(e.details.filter2.toLowerCase()) : true;
        const favoriteTransportsFilter = fullFilters.favorite_transports ? fullFilters.favorite_transports.includes(e.details.transportType) : true;
        const serviceFilter = fullFilters.services ? fullFilters.services.includes(e.details.service) : true;
        const seniorityFilter = fullFilters.seniorities ? fullFilters.seniorities.includes(e.details.seniority) : true;

        return contractTypeFilter && filter1Filter && filter2Filter && favoriteTransportsFilter && serviceFilter && seniorityFilter;
      });

      this.accountedAddresses = filteredCollaborators.length;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.counter-container {
  height: 80px;
  padding: 8px 16px;
  margin: 24px 0 0;
  border: solid 1px colors.$clear-border-color;
  border-right: 0;
  border-left: 0;
  display: flex;
  align-items: center;

  .counter {
    flex-shrink: 0;
    font-size: 48px;
    margin-right: 32px;
    font-family: 'Golden Plains';

    .active {
      display: inline-block;
      width: 64px;
      text-align: right;
      color: colors.$main-color;
    }
    .divider {
      color: colors.$earth-color;
      margin: 0 8px;
    }
    .total {
      color: colors.$dark-color;
    }
  }

  .text {
  }
}
</style>
